import React from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Form,
  FormGroup,
  FormFeedback,
  Label,
  Input,
  Col,
} from "reactstrap";
import { css } from "react-emotion";
import { Formik } from "formik";
import { generate } from "@/util/emvqr";
import QrCode from "qrcode.react";
import { saveAs } from "file-saver";

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

const modalStyles = css`
  position: absolute;
  top: 50%;
  left: 50%;
  right: auto;
  bottom: auto;
  overflow: visible;
  margin-right: -50%;
  transform: translate(-50%, -50%);
  width: 35rem;

  .buttons {
    display: flex;
    justify-content: space-between;
    margin: 15px 0 0;
  }
`;

const overlayStyles = css`
  position: fixed;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  background-color: rgba(255, 255, 255, 0.75);
  z-index: 1021; /* above navs, below sweetalert */
`;

const qrCodeStyles = css`
  display: flex;
  justify-content: center;
`;

class GenerateQrModal extends React.Component {
  state = {
    form: {
      recipientId: "",
      currencyAmount: 0,
      currencyName: "",
    },
    step: 0,
    qrCode: "",
  };

  baseState = {
    ...this.state,
  };

  validateFormik = ({ billNumber, description }) => {
    const errors = {};

    if (billNumber && billNumber.length > 25) {
      errors.billNumber = "Bill number's maximum length is 25 symbols";
    }

    if (description && description.length > 25) {
      errors.description = "Description's maximum length is 25 symbols";
    }

    return errors;
  };

  onGenerateQrCode = ({
    accountId,
    organizationName,
    wallet,
    amount,
    billNumber,
    description,
  }) => {
    const qrCode = generate({
      recipientId: accountId,
      merchantName: organizationName,
      currencyName: wallet,
      currencyAmount: amount,
      billNumber,
      description,
    });
    this.setState({
      form: {
        recipientId: accountId,
        currencyAmount: amount,
        currencyName: wallet,
      },
      qrCode,
      step: 1,
    });
  };

  onClickDownload = () => {
    const { recipientId, currencyAmount, currencyName } = this.state.form;
    const filename = `${recipientId}-${
      currencyAmount ? currencyAmount : ""
    }${currencyName}.png`;

    document.getElementById("qrCode").toBlob(blob => {
      saveAs(blob, filename);
    });
  };

  onClickPrint = async () => {
    const dataUrl = document.getElementById("qrCode").toDataURL();
    const content = `
      <html>
        <head><title>Print QrCode</title></head>
        <body>
          <img src="${dataUrl}" width="256" height="256">
        </body>
      </html>
      `;
    const win = window.open("", "", "width=800,height=600");
    win.document.open();
    await sleep(500);
    win.document.write(content);
    await sleep(500);
    win.document.close();
    win.focus();
    win.print();
  };

  reset = () => {
    this.setState(this.baseState);
  };

  beforeClose = () => {
    this.reset();
    this.props.onModalClose();
  };

  render() {
    const wallets = {
      KHR: "KHR",
      USD: "USD",
    };

    const form = (
      <Formik
        initialValues={{
          accountId: this.props.userAccountId,
          organizationName: this.props.organizationName,
          wallet: wallets.KHR,
        }}
        validate={this.validateFormik}
        onChange={this.validateFormik}
        onSubmit={this.onGenerateQrCode}>
        {({
          values,
          handleSubmit,
          handleChange,
          handleBlur,
          errors,
          setErrors,
        }) => (
          <Form onSubmit={handleSubmit}>
            <CardBody>
              <FormGroup row>
                <Label sm={3}>User account ID</Label>
                <Col sm={9}>
                  <Input
                    type="text"
                    name="accountId"
                    placeholder="Enter user's account id"
                    invalid={Boolean(errors.accountId)}
                    value={values.accountId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled
                  />
                  {errors.accountId && (
                    <FormFeedback className="text-alert">
                      {errors.accountId}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>Wallet</Label>
                <Col sm={9}>
                  <Input
                    type="select"
                    name="wallet"
                    placeholder="Select user's wallet"
                    invalid={Boolean(errors.wallet)}
                    value={values.wallet}
                    onChange={handleChange}
                    onBlur={handleBlur}>
                    <option>{wallets.KHR}</option>
                    <option>{wallets.USD}</option>
                  </Input>
                  {errors.wallet && (
                    <FormFeedback className="text-alert">
                      {errors.wallet}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>Amount</Label>
                <Col sm={9}>
                  <Input
                    type="text"
                    name="amount"
                    placeholder="Enter user's amount"
                    invalid={Boolean(errors.amount)}
                    value={values.amount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.amount && (
                    <FormFeedback className="text-alert">
                      {errors.amount}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>Bill number</Label>
                <Col sm={9}>
                  <Input
                    type="text"
                    name="billNumber"
                    placeholder="Enter user's bill number"
                    invalid={Boolean(errors.billNumber)}
                    value={values.billNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.billNumber && (
                    <FormFeedback className="text-alert">
                      {errors.billNumber}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
              <FormGroup row>
                <Label sm={3}>Description</Label>
                <Col sm={9}>
                  <Input
                    type="textarea"
                    name="description"
                    placeholder="Enter user's description"
                    invalid={Boolean(errors.description)}
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {errors.description && (
                    <FormFeedback className="text-alert">
                      {errors.description}
                    </FormFeedback>
                  )}
                </Col>
              </FormGroup>
            </CardBody>
            <CardFooter>
              <div className="buttons">
                <Button color="primary" type="submit">
                  <span>Show QR code</span>
                </Button>
              </div>
            </CardFooter>
          </Form>
        )}
      </Formik>
    );

    const qrCode = (
      <Form>
        <CardBody className={qrCodeStyles}>
          <QrCode id="qrCode" size={256} value={this.state.qrCode} />
        </CardBody>
        <CardFooter>
          <div className="buttons">
            <Button onClick={this.onClickPrint} color="primary">
              <span>Print</span>
            </Button>
            <Button onClick={this.onClickDownload} color="primary">
              <span>Download</span>
            </Button>
          </div>
        </CardFooter>
      </Form>
    );

    return (
      <Modal
        isOpen={this.props.isOpen}
        onRequestClose={this.beforeClose}
        className={modalStyles}
        overlayClassName={overlayStyles}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}>
        <Card>
          <CardHeader>
            <Button close onClick={this.beforeClose} />

            <span>Generate QR code</span>
          </CardHeader>
          {this.state.step === 0 ? form : qrCode}
        </Card>
      </Modal>
    );
  }
}

GenerateQrModal.defaultProps = {
  isOpen: false,
  onModalClose: () => {},
  userAccountId: "",
  organizationName: "",
};

GenerateQrModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onModalClose: PropTypes.func.isRequired,
  userAccountId: PropTypes.string.isRequired,
  organizationName: PropTypes.string.isRequired,
};

export default GenerateQrModal;
